import { useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";

import Spinner from "../../../shared/ui/Loading/Spinner";
import EmptyState from "../../../shared/ui/EmptyState/EmptyState";
import {
  messageFromError,
  parseDrfFieldErrors,
} from "../../../shared/api/drfErrors";

import {
  addPropertyFeature,
  deleteProperty,
  fetchFeatureCatalog,
  fetchPropertyDetail,
  patchPropertyCore,
  patchPropertyStatus,
  putPropertyCommercial,
  putPropertyLand,
  putPropertyLocation,
  putPropertyResidential,
  removePropertyFeature,
} from "../api/realty";

import EditHeader from "./EditHeader";
import EditTOC from "./EditTOC";
import EditSectionType from "./EditSectionType";
import EditSectionLocation from "./EditSectionLocation";
import EditSectionSpecs from "./EditSectionSpecs";
import EditSectionFeatures from "./EditSectionFeatures";
import EditSectionMedia from "./EditSectionMedia";
import EditSectionDescription from "./EditSectionDescription";
import EditSectionManage from "./EditSectionManage";
import EditSaveBar from "./EditSaveBar";
import EditManageModals from "./EditManageModals";
import UnsavedChangesDialog from "./UnsavedChangesDialog";

import {
  buildEditBaselineSnapshot,
  isEditFormDirty,
} from "../lib/editFormDirty";
import { useUnsavedChangesGuard } from "../hooks/useUnsavedChangesGuard";
import "./editUnsavedDialog.css";

import {
  buildLocationApiPayload,
  enrichLocationFromApi,
  needsLocationUpgrade,
  shouldValidateCanonicalOnEdit,
  validateLocationCanonical,
  validateLocationCoordinates,
  validateLocationStructured,
} from "../../geo/lib/locationGeo";
import { collectSpecsFieldIssues } from "../../createRealty/lib/wizardContract";
import {
  getStatusSuccessMessage,
} from "../../realty/lib/propertyLifecycle";
import {
  buildCooperationOfferPayload,
  hydrateCooperationFormFields,
  mergeCooperationOfferFieldErrors,
} from "../../realty/lib/cooperationOffer";
import LandParcelsEditor from "../../landParcels/widgets/LandParcelsEditor";
import { getApartmentTransitionBlockReason } from "../../landParcels/lib/landParcelContract";

// EditRealtyScreenContent — /realty/:id/edit canonical edit surface.
//
// Substrate: design/editRealty.html #Csection (.add-property-page
// .edit-property-page). Topology migrated per R6.1 directive; the
// shell + nav + chrome remain owned by frontend's RootLayout +
// MainLayout (not duplicated from the design HTML).
//
// Doctrine references:
//   * R3.9 — screen-level ownership derivation via String-coerced
//     `userInfo.id ?? public_id` === `property.created_by.id ??
//     public_id`. NO role systems, NO ACL, NO policy engine.
//   * R3.11A — status mutation + delete handlers mirror the detail
//     surface verbatim (one-shot await, no retry, no shared store).
//   * R3.3B — Bootstrap modal residue cleanup on pathname change /
//     unmount. Same lifecycle as RealtyDetailScreenContent.
//
// State containment:
//   * One `property` snapshot (backend truth) + a derived `form` shape
//     for editable values. No reducer, no schema engine. Local-only.
//   * Feature catalog fetched once on mount, then per-property selected
//     feature ids derived from property.features.
//   * Save is composite but inline: PATCH core + PUT location + PUT
//     active subtype + sync features deltas. Always-PUT (no diff
//     bookkeeping per R6.1 STEP R6.1.6) — backend upserts are idempotent.
//   * Status mutations + DELETE are independent of the save pipeline
//     (per design: applied immediately).

const SUBTYPE_BY_PROPERTY_TYPE = {
  apartment: "residential",
  house: "residential",
  land: "land",
  commercial: "commercial",
  industrial: "commercial",
};

// Editable core property fields (mirrors PropertyWriteSerializer
// fields minus `status`, which is mutated via the manage zone).
const CORE_EDITABLE_KEYS = [
  "title",
  "description_public",
  "description_private",
  "property_type",
  "deal_type",
  "price",
  "currency",
  "has_recovery_program",
  "has_housing_program",
  "video_url",
];

const LOCATION_KEYS = [
  "local_area",
  "address_object_id",
  "manual_address_object",
  "building_number",
  "legacy_address",
  "latitude",
  "longitude",
];

const RESIDENTIAL_KEYS = [
  "total_area_m2",
  "living_area_m2",
  "kitchen_area_m2",
  "rooms",
  "bathrooms",
  "floor",
  "total_floors",
  "year_built",
  // RIC-3.0 — house land-plot area + building characteristics + ЖК.
  "land_area_m2",
  "walls",
  "heating",
  "condition",
  "furniture",
  "residential_complex",
  "has_balcony",
  "has_parking",
];

const LAND_KEYS = [
  "area_m2",
  "purpose",
  "has_road_access",
  "has_utilities",
];

const COMMERCIAL_KEYS = [
  "total_area_m2",
  "floor",
  "suitable_for",
  // RIC-3.0 — building characteristics (commercial + industrial).
  "walls",
  "heating",
  "condition",
  "furniture",
];

const pickKeys = (source, keys) => {
  const out = {};
  if (!source) {
    for (const k of keys) out[k] = "";
    return out;
  }
  for (const k of keys) {
    const v = source[k];
    if (v === null || v === undefined) {
      out[k] = "";
    } else {
      out[k] = v;
    }
  }
  return out;
};

const buildFormFromProperty = (property) => {
  if (!property) return null;
  return {
    core: {
      ...pickKeys(property, CORE_EDITABLE_KEYS),
      ...hydrateCooperationFormFields(property.cooperation_offer),
    },
    location: enrichLocationFromApi(property.location),
    residential: pickKeys(property.residential, RESIDENTIAL_KEYS),
    land: pickKeys(property.land, LAND_KEYS),
    commercial: pickKeys(property.commercial, COMMERCIAL_KEYS),
  };
};

const buildSelectedFeatureIds = (property) => {
  const features = (property && property.features) || [];
  return new Set(features.map((f) => f.id));
};

// Coerce blank strings to null for nullable numeric fields. Sending
// "" to a DRF DecimalField raises a validation error; null is the
// correct empty-value transport.
const coerceNullable = (value) => {
  if (value === "" || value === undefined) return null;
  return value;
};

const buildCorePayload = (core) => ({
  title: core.title,
  description_public: core.description_public,
  description_private: core.description_private,
  property_type: core.property_type,
  deal_type: core.deal_type,
  price: core.price,
  currency: core.currency,
  has_recovery_program: Boolean(core.has_recovery_program),
  has_housing_program: Boolean(core.has_housing_program),
  video_url: core.video_url || "",
  cooperation_offer: buildCooperationOfferPayload(core),
});

const buildLocationPayload = (location) =>
  buildLocationApiPayload(location, coerceNullable);

const buildResidentialPayload = (res) => ({
  total_area_m2: res.total_area_m2,
  living_area_m2: coerceNullable(res.living_area_m2),
  kitchen_area_m2: coerceNullable(res.kitchen_area_m2),
  rooms: coerceNullable(res.rooms),
  bathrooms: coerceNullable(res.bathrooms),
  floor: coerceNullable(res.floor),
  total_floors: coerceNullable(res.total_floors),
  year_built: coerceNullable(res.year_built),
  land_area_m2: coerceNullable(res.land_area_m2),
  walls: res.walls || "",
  heating: res.heating || "",
  condition: res.condition || "",
  furniture: res.furniture || "",
  residential_complex: res.residential_complex || "",
  has_balcony: Boolean(res.has_balcony),
  has_parking: Boolean(res.has_parking),
});

const buildLandPayload = (land) => ({
  area_m2: land.area_m2,
  purpose: land.purpose,
  has_road_access: Boolean(land.has_road_access),
  has_utilities: Boolean(land.has_utilities),
});

const buildCommercialPayload = (com) => ({
  total_area_m2: com.total_area_m2,
  floor: coerceNullable(com.floor),
  suitable_for: com.suitable_for,
  walls: com.walls || "",
  heating: com.heating || "",
  condition: com.condition || "",
  furniture: com.furniture || "",
});

// RIC-2.2.2 — per-action toast copy delegated to propertyLifecycle helper.
const STATUS_ERROR_MESSAGES = {
  active: "Не вдалося опублікувати оголошення.",
};
const STATUS_ERROR_FALLBACK = "Не вдалося змінити статус оголошення.";

// RIC-2.2.5 / RIC-2.2.6 — statuses whose backend Property.clean() requires
// a complete listing (it returns early only for DRAFT; every other status
// needs location + the matching subtype row). These are the targets the
// lightweight pre-flight gate guards.
const COMPLETENESS_REQUIRED_STATUSES = new Set(["active", "sold"]);

// RIC-2.2.5 / RIC-2.2.6 — lightweight completeness probe. Deliberately NOT
// a second validation engine: it mirrors ONLY the two hasattr() checks
// Property.clean() performs (location presence + subtype-row presence)
// against the persisted `property` snapshot — the state the backend will
// actually see, since status mutations bypass the unsaved form. Field-level
// rules (required areas, etc.) stay owned by the backend serializers.
const collectMissingForListing = (property) => {
  const missing = [];
  if (!property || !property.location) {
    missing.push("локацію");
  }
  const subtype = property
    ? SUBTYPE_BY_PROPERTY_TYPE[property.property_type]
    : null;
  if (subtype && !property[subtype]) {
    missing.push("характеристики об'єкта");
  }
  return missing;
};

// RIC-2.3 / PHASE-1-5 Child 3 — frontend convenience mirror of the
// authoritative backend hard integrity rules (price>0, area>0, rooms>0,
// total_floors>0, floor<=total_floors, living/kitchen<=total). The backend
// serializers remain the source of truth; this only gives immediate
// feedback and avoids a guaranteed 400. Keyed by the same input field
// names so errors render inline. Empty / non-numeric values are skipped
// — they are handled by the backend's required / type validation, not
// duplicated here.
const collectFieldIssues = (form) => {
  const issues = {};
  const num = (v) =>
    v === "" || v === null || v === undefined ? null : Number(v);
  const isBad = (n) => n !== null && Number.isFinite(n) && n <= 0;

  if (isBad(num(form.core.price))) {
    issues.price = "Ціна має бути більшою за 0.";
  }

  Object.assign(issues, collectSpecsFieldIssues(form));
  return issues;
};

function EditRealtyScreenContent() {
  const { id } = useParams();
  const { pathname, hash } = useLocation();
  const navigate = useNavigate();

  // R3.9 — single auth read at screen level (foundation-frozen
  // userLogin slice).
  const userInfo = useSelector(
    (state) => state.userLogin && state.userLogin.userInfo,
  );

  // R8.2.7 — screen-local anon-entry guard. The bootstrap gate in App.js
  // prevents pre-hydration mount, but direct entry to /realty/:id/edit
  // without a session still falls through to fetchPropertyDetail → 401
  // → coordinator → performLogout. That cascade works but renders a
  // Spinner flash + wastes one round-trip. Local redirect eliminates both.
  // No foundation modification — coordinator still handles session-mid
  // -mutation paths and the non-owner gate below still handles authed-
  // but-not-owner.
  useEffect(() => {
    if (!userInfo) {
      unsavedBypassRef.current = true;
      navigate("/login", { replace: true });
    }
  }, [userInfo, navigate]);

  // Backend snapshot.
  const [property, setProperty] = useState(null);
  const [loadState, setLoadState] = useState("loading");
  const [loadError, setLoadError] = useState(null);

  // Local editable form state (mirrors `property`).
  const [form, setForm] = useState(null);
  const [selectedFeatureIds, setSelectedFeatureIds] = useState(() => new Set());
  const [savedBaseline, setSavedBaseline] = useState(null);

  // Save lifecycle (local).
  const [saveState, setSaveState] = useState("idle"); // idle | saving | success | error
  const [saveError, setSaveError] = useState(null);
  const [fieldErrors, setFieldErrors] = useState({});

  // Status + delete mutation lifecycle (independent of save).
  const [isMutatingStatus, setIsMutatingStatus] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // R8.7 B-1 — Closure-race guards for destructive double-click. Read
  // synchronously inside the handler; flipped before any await. The
  // useState booleans above remain the source of truth for the
  // disabled prop on the modal confirm buttons (React-rendered) — the
  // refs ONLY close the gap between the first click and the next
  // render where the state-derived disable applies. Status mutations
  // share one ref (one in-flight at a time across unpublish/sold/
  // archive) and delete keeps its own ref so a failed status attempt
  // cannot block a subsequent delete attempt (mirrors the screen's
  // existing isMutatingStatus/isDeleting orthogonality).
  const mutatingStatusRef = useRef(false);
  const persistInFlightRef = useRef(false);
  const deletingRef = useRef(false);
  const unsavedBypassRef = useRef(false);

  // Feature catalog (fetched once on mount).
  const [featureCatalog, setFeatureCatalog] = useState([]);
  const [catalogState, setCatalogState] = useState("loading");
  const [catalogError, setCatalogError] = useState(null);

  // R8.3.2 — timer cleanup containment for the post-save saveState
  // revert. Ref-tracked so unmount and a follow-up save both clear
  // the prior pending revert.
  const saveSuccessTimerRef = useRef(null);

  // Hydrate detail.
  useEffect(() => {
    if (!id) return undefined;
    if (!userInfo) return undefined;
    let cancelled = false;
    setLoadState("loading");
    setLoadError(null);

    fetchPropertyDetail(id)
      .then((data) => {
        if (cancelled) return;
        const nextForm = buildFormFromProperty(data);
        const nextFeatures = buildSelectedFeatureIds(data);
        setProperty(data);
        setForm(nextForm);
        setSelectedFeatureIds(nextFeatures);
        setSavedBaseline(buildEditBaselineSnapshot(nextForm, nextFeatures));
        setLoadState("loaded");
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(messageFromError(err));
        setLoadState("error");
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  // Hydrate feature catalog.
  useEffect(() => {
    if (!userInfo) return undefined;
    let cancelled = false;
    setCatalogState("loading");
    fetchFeatureCatalog()
      .then((data) => {
        if (cancelled) return;
        const rows = Array.isArray(data) ? data : [];
        setFeatureCatalog(rows);
        setCatalogState("loaded");
        setCatalogError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        setCatalogError(messageFromError(err));
        setCatalogState("error");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  // R3.3B — Bootstrap modal residue cleanup (mirror DetailScreenContent).
  useEffect(() => {
    return () => {
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("padding-right");
      document
        .querySelectorAll(".modal-backdrop")
        .forEach((el) => el.parentNode?.removeChild(el));
    };
  }, [pathname]);

  // R8.3.2 — clear pending save-revert timer on unmount.
  useEffect(() => {
    return () => {
      if (saveSuccessTimerRef.current) {
        window.clearTimeout(saveSuccessTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (loadState !== "loaded" || !form || hash !== "#sec-location") {
      return undefined;
    }
    const section = document.getElementById("sec-location");
    if (!section) return undefined;
    const heading = document.getElementById("ap-pane-location");
    section.scrollIntoView({ block: "start" });
    const focusTarget = heading || section;
    if (!focusTarget.hasAttribute("tabindex")) {
      focusTarget.setAttribute("tabindex", "-1");
    }
    focusTarget.focus({ preventScroll: true });
    return undefined;
  }, [loadState, form, hash]);

  // R3.9 — ownership derivation. Same predicate as detail surface.
  const currentUserId = useMemo(
    () => (userInfo && (userInfo.id || userInfo.public_id)) || null,
    [userInfo],
  );
  const ownerId = useMemo(() => {
    if (!property || !property.created_by) return null;
    return property.created_by.id || property.created_by.public_id || null;
  }, [property]);
  const isOwner =
    currentUserId !== null &&
    ownerId !== null &&
    String(ownerId) === String(currentUserId);

  const isDirty =
    loadState === "loaded"
    && Boolean(isOwner)
    && Boolean(form)
    && isEditFormDirty(savedBaseline, form, selectedFeatureIds);

  const cancelHref =
    property && property.id != null ? `/realty/${property.id}` : null;

  const unsavedGuard = useUnsavedChangesGuard({
    when: isDirty && !isDeleting,
    bypassRef: unsavedBypassRef,
    fallbackTo: cancelHref,
  });

  const dismissFieldErrorsForKeys = (keys) => {
    if (!keys || !keys.length) return;
    setFieldErrors((prev) => {
      let changed = false;
      const next = { ...prev };
      for (const key of keys) {
        if (next[key] !== undefined) {
          delete next[key];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  };

  const changedKeys = (prevObj, nextObj) => {
    const keys = new Set([
      ...Object.keys(prevObj || {}),
      ...Object.keys(nextObj || {}),
    ]);
    const out = [];
    for (const key of keys) {
      if ((prevObj || {})[key] !== (nextObj || {})[key]) out.push(key);
    }
    return out;
  };

  // Section change handlers — partial-merge into the form shape.
  const handleCoreChange = (next) => {
    setForm((prev) => {
      if (!prev) return prev;
      dismissFieldErrorsForKeys(changedKeys(prev.core, next));
      return { ...prev, core: next };
    });
  };
  const handleLocationChange = (next) => {
    setForm((prev) => {
      if (!prev) return prev;
      dismissFieldErrorsForKeys(changedKeys(prev.location, next));
      return { ...prev, location: next };
    });
  };
  const handleResidentialChange = (next) => {
    setForm((prev) => {
      if (!prev) return prev;
      dismissFieldErrorsForKeys(changedKeys(prev.residential, next));
      return { ...prev, residential: next };
    });
  };
  const handleLandChange = (next) => {
    setForm((prev) => {
      if (!prev) return prev;
      dismissFieldErrorsForKeys(changedKeys(prev.land, next));
      return { ...prev, land: next };
    });
  };
  const handleCommercialChange = (next) => {
    setForm((prev) => {
      if (!prev) return prev;
      dismissFieldErrorsForKeys(changedKeys(prev.commercial, next));
      return { ...prev, commercial: next };
    });
  };

  const handleToggleFeature = (featureId) => {
    setSelectedFeatureIds((prev) => {
      const next = new Set(prev);
      if (next.has(featureId)) next.delete(featureId);
      else next.add(featureId);
      return next;
    });
  };

  // RIC-3.1.2 — re-fetch after an image mutation so property.images stays
  // the source of truth. Only `property` is refreshed; `form` (including an
  // unsaved video_url edit) is untouched — images are not part of the form.
  const handleMediaChanged = async () => {
    const refreshed = await fetchPropertyDetail(id);
    setProperty(refreshed);
  };

  const handleLandParcelsChanged = (landParcels) => {
    setProperty((previous) =>
      previous ? { ...previous, land_parcels: landParcels } : previous,
    );
  };

  // Composite save. Inline orchestration (NOT a framework).
  //
  // Order:
  //   1. PATCH core property fields.
  //   2. PUT location.
  //   3. PUT active subtype (residential | land | commercial).
  //   4. Sync feature deltas (POST adds, DELETE removes).
  // On any failure: stop, surface error, leave server in whatever
  // partial state was reached. (Per directive STEP R6.1 — no
  // transaction orchestration, no rollback engine.)
  //
  // Ordinary Save and dirty Publish share this path so publish cannot
  // drift onto a second payload builder.
  const persistEditForm = async ({ successToast = true } = {}) => {
    if (!form || persistInFlightRef.current || saveState === "saving") {
      return { ok: false, reason: "busy" };
    }
    if (!isEditFormDirty(savedBaseline, form, selectedFeatureIds)) {
      return { ok: false, reason: "clean" };
    }

    const apartmentTransitionBlock = getApartmentTransitionBlockReason(
      property && property.land_parcels,
      form.core.property_type,
    );
    if (apartmentTransitionBlock) {
      setFieldErrors({ property_type: apartmentTransitionBlock });
      setSaveError(apartmentTransitionBlock);
      setSaveState("error");
      toast.error(apartmentTransitionBlock);
      return { ok: false, reason: "validation" };
    }

    // RIC-2.3 — pre-save convenience mirror of the backend numeric rules.
    // Abort before the network round-trip when an obviously invalid value
    // is present; the backend serializers stay the authoritative gate.
    const issues = collectFieldIssues(form);
    if (shouldValidateCanonicalOnEdit(form.location)) {
      Object.assign(issues, validateLocationCanonical(form.location));
    }
    Object.assign(issues, validateLocationStructured(form.location));
    Object.assign(issues, validateLocationCoordinates(form.location));
    if (Object.keys(issues).length > 0) {
      setFieldErrors(issues);
      setSaveError("Виправте позначені поля перед збереженням.");
      setSaveState("error");
      toast.error("Виправте позначені поля перед збереженням.");
      return { ok: false, reason: "validation" };
    }

    persistInFlightRef.current = true;
    setSaveState("saving");
    setSaveError(null);
    setFieldErrors({});

    const upgradingLocation = shouldValidateCanonicalOnEdit(form.location);

    try {
      const coreResp = await patchPropertyCore(
        property.id,
        buildCorePayload(form.core),
      );
      await putPropertyLocation(property.id, buildLocationPayload(form.location));

      const subtype = SUBTYPE_BY_PROPERTY_TYPE[form.core.property_type];
      if (subtype === "residential") {
        await putPropertyResidential(
          property.id,
          buildResidentialPayload(form.residential),
        );
      } else if (subtype === "land") {
        await putPropertyLand(property.id, buildLandPayload(form.land));
      } else if (subtype === "commercial") {
        await putPropertyCommercial(
          property.id,
          buildCommercialPayload(form.commercial),
        );
      }

      // Features delta sync. Baseline = property.features at last
      // fetch; selection = local Set.
      const baselineIds = buildSelectedFeatureIds(property);
      const toAdd = [];
      const toRemove = [];
      for (const fid of selectedFeatureIds) {
        if (!baselineIds.has(fid)) toAdd.push(fid);
      }
      for (const fid of baselineIds) {
        if (!selectedFeatureIds.has(fid)) toRemove.push(fid);
      }
      for (const fid of toAdd) {
        // eslint-disable-next-line no-await-in-loop
        await addPropertyFeature(property.id, fid);
      }
      for (const fid of toRemove) {
        // eslint-disable-next-line no-await-in-loop
        await removeFeatureSafely(property.id, fid);
      }

      // Refetch authoritative state. Saves are NOT optimistic —
      // post-save we re-read the detail payload to converge on the
      // backend's view of nested subtype + features + updated_at.
      const refreshed = await fetchPropertyDetail(property.id);
      const nextForm = buildFormFromProperty(refreshed);
      const nextFeatures = buildSelectedFeatureIds(refreshed);
      setProperty(refreshed);
      setForm(nextForm);
      setSelectedFeatureIds(nextFeatures);
      setSavedBaseline(buildEditBaselineSnapshot(nextForm, nextFeatures));
      setSaveState("success");

      const upgradedLegacy =
        upgradingLocation
        && needsLocationUpgrade(property.location)
        && !needsLocationUpgrade(refreshed.location);
      if (successToast) {
        toast.success(
          upgradedLegacy
            ? "Збережено. Локацію уточнено."
            : "Зміни збережено.",
        );
      }

      // Eventual revert to idle so the relative "updated_at" label
      // can show again. R8.3.2 — ref-tracked so unmount and a
      // subsequent save both clear the prior pending revert. The
      // functional setter is still safe across unmount; the clear
      // is defense-in-depth against orphan setState.
      if (saveSuccessTimerRef.current) {
        window.clearTimeout(saveSuccessTimerRef.current);
      }
      saveSuccessTimerRef.current = window.setTimeout(() => {
        setSaveState((curr) => (curr === "success" ? "idle" : curr));
      }, 3000);
      // coreResp is intentionally unused after success — we re-read
      // the full detail payload above for nested + features parity.
      void coreResp;
      return { ok: true, property: refreshed };
    } catch (err) {
      console.error("EditRealtyScreenContent: save failed", err);
      const errorMessage = messageFromError(err);
      setSaveError(errorMessage);
      setFieldErrors(mergeCooperationOfferFieldErrors(
        err,
        parseDrfFieldErrors(err, [
          ...CORE_EDITABLE_KEYS,
          ...LOCATION_KEYS,
          "canonical_region",
          "canonical_settlement",
          ...RESIDENTIAL_KEYS,
          ...LAND_KEYS,
          ...COMMERCIAL_KEYS,
        ]),
      ));
      setSaveState("error");
      toast.error(errorMessage);
      return { ok: false, reason: "network" };
    } finally {
      persistInFlightRef.current = false;
    }
  };

  const handleSave = async (event) => {
    event.preventDefault();
    await persistEditForm({ successToast: true });
  };

  const applyStatusMutation = async (listing, nextStatus, successMessage) => {
    if (!listing) return false;
    if (COMPLETENESS_REQUIRED_STATUSES.has(nextStatus)) {
      const missing = collectMissingForListing(listing);
      if (missing.length > 0) {
        toast.error(`Спочатку заповніть: ${missing.join(", ")}.`);
        return false;
      }
    }
    try {
      const previousStatus = listing.status;
      const updated = await patchPropertyStatus(listing.id, nextStatus);
      setProperty((prev) =>
        prev
          ? {
              ...prev,
              status: updated.status,
              is_moderation_locked:
                updated.is_moderation_locked ?? prev.is_moderation_locked,
            }
          : prev,
      );
      toast.success(
        successMessage
          || getStatusSuccessMessage(nextStatus, {
            dealType: listing.deal_type,
            fromStatus: previousStatus,
          }),
      );
      return true;
    } catch (err) {
      console.error("EditRealtyScreenContent: status mutation failed", err);
      toast.error(STATUS_ERROR_MESSAGES[nextStatus] || STATUS_ERROR_FALLBACK);
      return false;
    }
  };

  // Status mutation + delete handlers (mirror DetailScreen).
  const handleStatusMutation = async (nextStatus, successMessage) => {
    // R8.7 B-1 — synchronous ref guard closes the rapid-double-click
    // window before React re-renders the state-derived disable. One
    // ref serves all three status mutations (unpublish/sold/archive)
    // because EditManageModals disables them all on `anyInFlight`
    // (isMutatingStatus || isDeleting); the ref preserves that
    // contract synchronously.
    if (mutatingStatusRef.current || !property) return;

    mutatingStatusRef.current = true;
    setIsMutatingStatus(true);
    try {
      await applyStatusMutation(property, nextStatus, successMessage);
    } finally {
      mutatingStatusRef.current = false;
      setIsMutatingStatus(false);
    }
  };

  // Dirty DRAFT publish: validate+persist the same composite save, then
  // PATCH status=active against the refreshed Property. Clean drafts
  // still publish directly. Other lifecycle actions do not auto-save.
  const handleConfirmPublish = async () => {
    if (mutatingStatusRef.current || !property) return;
    if (saveState === "saving" || persistInFlightRef.current) return;

    mutatingStatusRef.current = true;
    setIsMutatingStatus(true);
    try {
      let listing = property;
      const dirty =
        Boolean(form)
        && isEditFormDirty(savedBaseline, form, selectedFeatureIds);
      if (dirty) {
        const result = await persistEditForm({ successToast: false });
        if (!result.ok) return;
        listing = result.property;
      }
      await applyStatusMutation(listing, "active");
    } finally {
      mutatingStatusRef.current = false;
      setIsMutatingStatus(false);
    }
  };
  const handleConfirmUnpublish = () => handleStatusMutation("draft");
  const handleConfirmReserve = () => handleStatusMutation("reserved");
  const handleConfirmSold = () => handleStatusMutation("sold");
  const handleConfirmArchive = () => handleStatusMutation("archived");
  const handleConfirmReactivate = () => handleStatusMutation("active");

  // R8.5 Fix B — same containment as RealtyDetailScreenContent.handleConfirmDelete.
  // `replace: true` evicts /realty/:id/edit from the back stack so browser-back
  // does not land on a 404.
  //
  // R8.7.4 — Replaced `state.mayBeStale` navigation hint with a success
  // toast. /feed re-fetches on mount; the deleted entity is gone; the
  // prior banner overclaimed list staleness. Same conversion as
  // RealtyDetailScreenContent.handleConfirmDelete.
  const handleConfirmDelete = async () => {
    // R8.7 B-1 — synchronous ref guard closes the rapid-double-click
    // window before React re-renders the state-derived disable. On
    // success the component unmounts on navigate so the ref is
    // released only on the failure branch.
    if (deletingRef.current || !property) return;
    deletingRef.current = true;
    setIsDeleting(true);
    try {
      await deleteProperty(property.id);
      unsavedBypassRef.current = true;
      toast.success("Оголошення видалено.");
      navigate("/feed", { replace: true });
    } catch (err) {
      console.error("EditRealtyScreenContent: delete failed", err);
      toast.error("Не вдалося видалити оголошення.");
      deletingRef.current = false;
      setIsDeleting(false);
    }
  };

  // ---------- Render branches ----------

  if (loadState === "loading") {
    return (
      <main
        className="add-property-page edit-property-page"
        id="Csection"
        tabIndex="-1"
      >
        <div className="container">
          <Spinner size="lg" label="Завантаження оголошення" />
        </div>
      </main>
    );
  }

  if (loadState === "error" || !property) {
    return (
      <main
        className="add-property-page edit-property-page"
        id="Csection"
        tabIndex="-1"
      >
        <div className="container">
          <EmptyState
            icon="fa-solid fa-triangle-exclamation"
            title="Не вдалося завантажити оголошення"
            description={loadError || "Спробуйте оновити сторінку."}
            size="md"
          />
        </div>
      </main>
    );
  }

  // R3.9 — Non-owner gate. Render an unauthorized state inside the
  // screen content rather than redirecting. NO auth wrapper, NO
  // route-level guard infrastructure per directive STEP R6.1.10.
  if (!isOwner) {
    return (
      <main
        className="add-property-page edit-property-page"
        id="Csection"
        tabIndex="-1"
      >
        <div className="container">
          <EmptyState
            icon="fa-solid fa-lock"
            title="Редагування недоступне"
            description="Лише власник оголошення може його редагувати."
            size="md"
          />
        </div>
      </main>
    );
  }

  if (!form) {
    // Defensive: owner gate passed but form not hydrated yet — keep #Csection.
    return (
      <main
        className="add-property-page edit-property-page"
        id="Csection"
        tabIndex="-1"
      >
        <div className="container">
          <Spinner size="lg" label="Завантаження форми редагування" />
        </div>
      </main>
    );
  }

  return (
    <main
      className="add-property-page edit-property-page"
      id="Csection"
      tabIndex="-1"
      data-property-type={form.core.property_type}
      data-deal-type={form.core.deal_type}
      data-status={property.status}
    >
      <div className="container">
        <EditHeader property={property} />
        <EditTOC />

        <form className="ap-form" onSubmit={handleSave} noValidate>
          {/* RIC-2.2.3 / RIC-2.2.4 — property_type and deal_type are
              immutable after creation. Changing property_type would orphan
              the incompatible subtype row; changing deal_type alters the
              listing's business meaning. Both are locked unconditionally on
              the edit surface (creation still chooses them in the wizard).
              This supersedes the R6.2A draft-only property_type lock. */}
          <EditSectionType
            values={form.core}
            onChange={handleCoreChange}
            fieldErrors={fieldErrors}
            propertyTypeLocked
            dealTypeLocked
          />
          <EditSectionLocation
            propertyId={property.id}
            values={form.location}
            onChange={handleLocationChange}
            fieldErrors={fieldErrors}
          />
          <EditSectionSpecs
            propertyType={form.core.property_type}
            residential={form.residential}
            land={form.land}
            commercial={form.commercial}
            onChangeResidential={handleResidentialChange}
            onChangeLand={handleLandChange}
            onChangeCommercial={handleCommercialChange}
            fieldErrors={fieldErrors}
          />
          <LandParcelsEditor
            propertyId={property.id}
            propertyType={form.core.property_type}
            initialParcels={property.land_parcels}
            onParcelsChange={handleLandParcelsChanged}
            disabled={saveState === "saving"}
            apartmentTransitionBlocked={getApartmentTransitionBlockReason(
              property.land_parcels,
              form.core.property_type,
            )}
          />
          <EditSectionFeatures
            catalog={featureCatalog}
            catalogState={catalogState}
            catalogError={catalogError}
            selectedIds={selectedFeatureIds}
            onToggle={handleToggleFeature}
          />
          <EditSectionMedia
            property={property}
            onMediaChanged={handleMediaChanged}
            videoUrl={form.core.video_url}
            onVideoUrlChange={(value) =>
              handleCoreChange({ ...form.core, video_url: value })
            }
          />
          <EditSectionDescription
            values={form.core}
            onChange={handleCoreChange}
            fieldErrors={fieldErrors}
          />
          <EditSectionManage
            property={property}
            isMutatingStatus={isMutatingStatus}
            isDeleting={isDeleting}
          />

          {fieldErrors._form ? (
            <p className="ap-helper text-danger" role="alert">
              {fieldErrors._form}
            </p>
          ) : null}

          <EditSaveBar
            cancelHref={cancelHref}
            saveState={saveState}
            saveError={saveError}
            updatedAt={property.updated_at}
            isDirty={isDirty}
          />
        </form>
      </div>

      <UnsavedChangesDialog
        open={unsavedGuard.isPromptOpen}
        onStay={unsavedGuard.stay}
        onDiscard={unsavedGuard.discard}
      />

      <EditManageModals
        property={property}
        onConfirmPublish={handleConfirmPublish}
        onConfirmUnpublish={handleConfirmUnpublish}
        onConfirmReserve={handleConfirmReserve}
        onConfirmSold={handleConfirmSold}
        onConfirmArchive={handleConfirmArchive}
        onConfirmReactivate={handleConfirmReactivate}
        onConfirmDelete={handleConfirmDelete}
        isMutatingStatus={isMutatingStatus}
        isDeleting={isDeleting}
      />
    </main>
  );
}

// Local helper for delete-feature error tolerance. The backend returns
// 404 for an already-removed (property, feature) pair; we treat that
// as a no-op rather than failing the whole save.
async function removeFeatureSafely(propertyId, featureId) {
  try {
    await removePropertyFeature(propertyId, featureId);
  } catch (err) {
    const status = err && err.response && err.response.status;
    if (status === 404) return; // already gone — fine
    throw err;
  }
}

export default EditRealtyScreenContent;
